import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

import { BankAccountTypeSchema } from './use-load-bank-accounts';

const URL = '/bank_accounts';

// The full desired set of manual accounts is sent on every save: existing
// accounts carry their `id`, new ones omit it, and any previously-existing
// account left out is destroyed server-side. Synced (Ponto/Codabox) accounts are
// never edited: the default pointer may reference them, and Codabox accounts
// listed in `archivedCodaboxBankAccountIds` are archived.
const UpdateBankAccountsVariablesSchema = z.object({
  companyBankAccounts: z.array(
    z.object({
      id: z.string().optional(),
      iban: z.string().min(1),
      bic: z.string(),
    }),
  ),
  defaultBankAccountId: z.number().nullable(),
  defaultBankAccountType: BankAccountTypeSchema.nullable(),
  archivedCodaboxBankAccountIds: z.array(z.string()),
});

export type UpdateBankAccountsVariables = z.infer<typeof UpdateBankAccountsVariablesSchema>;

export async function updateBankAccountsFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateBankAccountsVariables,
) {
  const instance = await axiosInstance;

  return instance.put(URL, UpdateBankAccountsVariablesSchema.parse(variables));
}

export function useUpdateBankAccounts() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateBankAccountsVariables>({
    mutationFn: variables => updateBankAccountsFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['bankAccounts'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateBankAccountsVariables,
    'updateBankAccounts'
  >('updateBankAccounts', mutation);
}
